import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { LinkLayout, LinkProps } from '../../../../../../Shared/Frontend/utils/links'
import { ActionProps, ImageSizesProps, ImageSourcesProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { RecentNewsBlockClassNames } from './recentNewsBlock.state'
import i18next from 'i18next'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { htmlDecode } from '../../../utils/html'

export const recentNewsBlockClasses: RecentNewsBlockClassNames = {
  // DEFAULTS
  block: 'block block--news',
  inview: 'inview',
  inner: 'block__inner ls-container ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  intro: 'block__intro text--l',
  content: 'block__content',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--secondary',

  // BLOCK SPECIFIC
  layout: 'block__layout',
  empty: 'block__message text--s',
  teasers: 'block__teasers teasers',
  teasersList: 'teasers__list',
  teasersItem: 'teasers__item',
  teaser: 'teaser teaser--news',
  teaserLink: 'teaser__link',
  teaserInner: 'teaser__inner',
  teaserContent: 'teaser__content',
  teaserMeta: 'teaser__meta',
  teaserTitle: 'teaser__title title--s',
  teaserVisual: 'teaser__visual',
  teaserImage: 'teaser__image'
}

export const RecentNewsBlockLayout = {
  Inner: (props: {
    id?: string
    children?: React.ReactNode
  }): JSX.Element => {
    const { children, id } = props
    return (
      <div className={getClassNameModern(recentNewsBlockClasses, 'layout', '')}>
        <div className={getClassNameModern(recentNewsBlockClasses, 'inner', 'mw-container-s mx-auto px-gutter')}>{children}</div>
      </div>
    )
  },
  Content: (props: {
    label?: string,
    title?: string,
    intro?: string,
    children?: React.ReactNode
    showActionAsLink: boolean
  } & ActionProps) => {
    const {
      label,
      title,
      intro,
      children,
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
      showActionAsLink,
    } = props
    return (
      <>
        {(label || title || intro) && (
          <header className={getClassNameModern(recentNewsBlockClasses, 'header', '')}>
            {label &&
              <p className={getClassNameModern(recentNewsBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>{label}</p>
            }
            {title &&
              <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(recentNewsBlockClasses, 'title', 'h2 mb-3')}>{title}</h2>
            }
            {intro &&
              <div
                className={getClassNameModern(recentNewsBlockClasses, 'intro', 'text--styled lead mb-3')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            }
          </header>
        )}

        <div className={getClassNameModern(recentNewsBlockClasses, 'content', '')}>{children}</div>

        {(showPrimary || showSecondary) && (
          <footer className={getClassNameModern(recentNewsBlockClasses, 'footer', 'pt-5')}>
            {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
              <div className={getClassNameModern(recentNewsBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}>
                <SharedBlockLayout.Clickable
                  id='textPrimaryAction'
                  type={showActionAsLink === true ? 'link' : 'button'}
                  link={primaryAction}
                  classNames={getClassNameModern(recentNewsBlockClasses, showActionAsLink === true ? 'clickableSecondary' : 'clickablePrimary', 'btn btn-primary')}
                  SVGIcon={showActionAsLink === true ? { iconId: "#icon--chevron-right", width: "7px", height:"11px" } : undefined}
                />
              </div>
            )}
            {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
              <div
                className={getClassNameModern(
                  recentNewsBlockClasses,
                  'clickableWrapper',
                  'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                )}
              >
                <SharedBlockLayout.Clickable
                  id='textSecondaryAction'
                  type='link'
                  link={secondaryAction}
                  classNames={getClassNameModern(recentNewsBlockClasses, 'clickableSecondary', 'btn btn-outline-primary')}
                  SVGIcon={{ iconId: "#icon--chevron-right", width: "7px", height:"11px" }}
                />
              </div>
            )}
          </footer>
        )}
      </>
    )
  },
  Teasers: (props: {
    children?: React.ReactNode
  }): JSX.Element => {
    const { children } = props
    return (
      <div className={getClassNameModern(recentNewsBlockClasses, 'teasers', '')}>
        <ul className={getClassNameModern(recentNewsBlockClasses, 'teasersList', '')}>{children}</ul>
      </div>
    )
  },
  Teaser: (props: {
    teaserKey: string,
    date: Date,
    title: string,
    intro?: string,
    link: LinkProps,
    image?: ImageSizesProps['image']
    imageSources?: ImageSourcesProps['sources']
  }): JSX.Element => {
    const { date, title, intro, link, image, imageSources, teaserKey } = props
    return (
      <li className={getClassNameModern(recentNewsBlockClasses, 'teasersItem', '')} key={teaserKey}>
        <article className={getClassNameModern(recentNewsBlockClasses, 'teaser', '')}>
          <LinkLayout
            link={props.link}
            className={getClassNameModern(recentNewsBlockClasses, 'teaserLink', '')}
          >
            <div className={getClassNameModern(recentNewsBlockClasses, 'teaserInner', '')}>
              <div className={getClassNameModern(recentNewsBlockClasses, 'teaserContent', '')}>
                <time dateTime={date.toISOString()} className={getClassNameModern(recentNewsBlockClasses, 'teaserMeta', '')}>{date.toLocaleString("nl-NL", {day: 'numeric', month: 'long', year: 'numeric'})}</time>
                <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(recentNewsBlockClasses, 'teaserTitle', '')}>
                  <span>{props.title}</span>
                </h2>
                {intro ? <p className='teaser__intro'>{intro.length > 150 ? intro.substring(0, 150) + '...' : intro}</p> : <></>}

                <footer className="teaser__footer">
                  <span className="teaser__fauxlink">{i18next.t('General:ReadMore')}</span>
                </footer>
              </div>

              <div className={getClassNameModern(recentNewsBlockClasses, 'teaserVisual', '')}>
                {image &&
                  <SharedBlockLayout.ModernImageSrcSet
                    image={image}
                    lazyload={true}
                    priority={undefined}
                    classNames={getClassNameModern(recentNewsBlockClasses, 'teaserImage', '')}
                    imageProportions={{
                      width: '310',
                      height: '206',
                      sizes: '(min-width: 768px) 310px, 30vw'
                    }}
                  />
                }
              </div>
            </div>
          </LinkLayout>
        </article>
      </li>
    )
  },
  Empty: (props: {}): JSX.Element => {
    return (
      <div className={getClassNameModern(recentNewsBlockClasses, 'empty', '')}>{i18next.t('General:NoResults')}</div>
    )
  },
}
